// The Visit Page (Session AQW, Phase 2, W-7): a service/call-out visit's own record page, keyed by
// the Job id, at /office/jobsheets/:id. Before this session a completed visit had no page of its
// own -- only a row-expander on Service Records / the Call-outs page's embedded records list
// (JobSheetList.tsx). This is that page: the full ServiceRecordPanel record (now richer -- the D78
// "Also covered by this contract" items and the RAMS acknowledgement both render here for the first
// time, for every existing caller of the panel too, since it is the one shared component), the
// approval action promoted to the header (mirroring what the panel's own embedded Approve control
// already does, so either control reaches the same idempotent server route), and cross-links to the
// lift/site/customer/contract this visit belongs to, plus its audit timeline.
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type { JobSheetListItem, AuditTimelineEntry, RemedialDefectOffer as RemedialDefectOfferItem } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import JobTypeBadge from "../../components/JobTypeBadge";
import { RecordHeader } from "../../components/RecordHeader";
import { AccountSymbol } from "../../components/AccountSymbol";
import { RecordLink, RecordLinkGroup } from "../../components/RecordLink";
import { JobStatePill } from "../../components/StatusPill";
import { JOB_HOLD_REASON_OPTIONS, type JobHoldReasonKey } from "../../lib/jobHold";
import { buttonClasses } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { ConfirmButton } from "../../components/ConfirmButton";
import { EmptyState } from "../../components/EmptyState";
import { RemedialDefectOffer } from "./RemedialDefectOffer";
import { AuditTimeline } from "../../components/AuditTimeline";
import { ServiceRecordPanel } from "./ServiceRecordPanel";
import { LiftContractDoor } from "./LiftContractDoor";

type BannerState = { kind: "ok" | "error"; message: string } | null;

export function VisitPage() {
  const { id } = useParams<{ id: string }>();

  const [sheet, setSheet] = useState<JobSheetListItem | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error" | "not_found">("loading");
  const [banner, setBanner] = useState<BannerState>(null);
  const [approving, setApproving] = useState(false);
  // Session ASF: what the approval said about the money and the timesheet. The embedded panel shows these only for
  // an approval made with its OWN control, so without this an approval from the header dropped them for good (a
  // repeat approval returns neither): the fixed-price repair note, and Session ASC's warning that an approved
  // repair estimate is on no job, so this job's draft bills hours and parts rather than the agreed price.
  //
  // Session ASK (the ASF review's INFO, the same shape as F1): defectsToReview too, the open defects this job was
  // raised to fix and the offer to resolve each one. The panel shows it only for its own approval and a repeat
  // approval returns none, so a header approval lost it for this page (the defect's own page still makes it).
  const [approvalNotes, setApprovalNotes] = useState<{
    invoiceNote: string | null;
    timesheetNote: string | null;
    defectsToReview: RemedialDefectOfferItem[];
  } | null>(null);

  const [entries, setEntries] = useState<AuditTimelineEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(true);
  const [auditError, setAuditError] = useState<string | null>(null);

  // Session AQY (D-AQX-1): pause (awaiting parts) / resume, reachable from this page too ("wherever
  // a job's status is shown or changed") once a sheet exists to open this page at all -- see the
  // route-selection note on api.jobsheets.get for why an untouched scheduled job has no page here.
  const [pausing, setPausing] = useState(false);
  const [pauseReason, setPauseReason] = useState("");
  // Session SC9A: the reason is picked from one fixed list; Other needs a few words.
  const [pauseHoldReason, setPauseHoldReason] = useState<JobHoldReasonKey>("awaiting_parts");
  const [pauseResumeBusy, setPauseResumeBusy] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setState("loading");
    try {
      const s = await api.jobsheets.get(id);
      setSheet(s);
      setState("ready");
    } catch (err) {
      setState(err instanceof ApiError && err.status === 404 ? "not_found" : "error");
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // The audit timeline is loaded independently of the record itself: a failure here must never
  // block the page from showing the record, and the record's own load must never wait on it.
  useEffect(() => {
    if (!id) return;
    let live = true;
    setAuditLoading(true);
    setAuditError(null);
    api.auditLog
      .list("job", id)
      .then((res) => {
        if (live) setEntries(res.entries);
      })
      .catch((err) => {
        if (live) setAuditError(friendlyApiError(err, "Could not load the activity for this record."));
      })
      .finally(() => {
        if (live) setAuditLoading(false);
      });
    return () => {
      live = false;
    };
  }, [id]);

  function ok(message: string) {
    setBanner({ kind: "ok", message });
  }
  function fail(message: string) {
    setBanner({ kind: "error", message });
  }

  // The SAME endpoint the embedded ServiceRecordPanel's own Approve control calls
  // (POST /api/jobsheets/:jobId/approve, idempotent: an already-approved job returns its current
  // state rather than erroring), promoted to the page header so approval does not require scrolling
  // into the panel first. Updating `sheet` here flows straight into the panel below, since it
  // derives its own "already approved" display from these same fields -- there is no second state
  // to keep in step.
  async function approveNow() {
    if (!sheet?.jobId) return;
    setApproving(true);
    try {
      const res = await api.jobsheets.approve(sheet.jobId);
      setSheet((s) =>
        s ? { ...s, jobStatus: "complete", emailSentAt: new Date().toISOString(), emailSentTo: res.emailSentTo, driveFileUrl: res.driveFileUrl } : s,
      );
      if (res.alreadyApproved) ok("Already released.");
      else {
        ok(res.draftNote ?? "Released.");
        setApprovalNotes({ invoiceNote: res.invoiceNote ?? null, timesheetNote: res.timesheetNote ?? null, defectsToReview: res.defectsToReview ?? [] });
      }
    } catch (err) {
      fail(friendlyApiError(err, "Could not release this record. Please try again."));
    } finally {
      setApproving(false);
    }
  }

  // Session AQY (D-AQX-1): the SAME route the Scheduling calendar's pause/resume buttons call.
  async function pauseNow() {
    if (!sheet?.jobId || (pauseHoldReason === "other" && !pauseReason.trim())) return;
    setPauseResumeBusy(true);
    try {
      const words = pauseHoldReason === "other" ? pauseReason.trim() : "";
      await api.schedule.pause(sheet.jobId, pauseHoldReason, words);
      setSheet((s) => (s ? { ...s, jobStatus: "parts_pending", holdReason: pauseHoldReason, holdReasonText: words || null } : s));
      setPausing(false);
      setPauseReason("");
      setPauseHoldReason("awaiting_parts");
      ok("On hold.");
    } catch (err) {
      fail(friendlyApiError(err, "Could not pause this visit. Please try again."));
    } finally {
      setPauseResumeBusy(false);
    }
  }

  async function resumeNow() {
    if (!sheet?.jobId) return;
    setPauseResumeBusy(true);
    try {
      await api.schedule.resume(sheet.jobId);
      setSheet((s) => (s ? { ...s, jobStatus: "in_progress", holdReason: null, holdReasonText: null } : s));
      ok("Resumed.");
    } catch (err) {
      fail(friendlyApiError(err, "Could not resume this visit. Please try again."));
    } finally {
      setPauseResumeBusy(false);
    }
  }

  if (state === "loading") {
    return (
      <div className="p-4 sm:p-6">
        <Loader shape="panel" />
      </div>
    );
  }

  if (state === "not_found") {
    return (
      <div className="p-4 sm:p-6">
        <EmptyState
          message="That visit record could not be found."
          action={
            <Link to="/office/jobsheets" className="text-sm text-ele-blue underline">
              Back to Service Records
            </Link>
          }
        />
      </div>
    );
  }

  if (state === "error" || !sheet) {
    return (
      <div className="p-4 sm:p-6">
        <EmptyState
          message="Could not load this record."
          action={
            <button type="button" onClick={() => void load()} className="text-sm text-ele-blue underline">
              Retry
            </button>
          }
        />
      </div>
    );
  }


  return (
    <div className="p-4 sm:p-6">
      <RecordHeader
        title={sheet.reference ?? sheet.ref}
        backTo="/office/jobsheets"
        backLabel="Back to Service Records"
        status={
          <JobStatePill
            jobId={sheet.jobId}
            facts={{
              status: sheet.jobStatus,
              entrapment: sheet.entrapment,
              holdReason: sheet.holdReason,
              holdReasonText: sheet.holdReasonText,
              followOnWorks: sheet.followOnWorks,
            }}
          />
        }
        subtitle={
          <>
            <JobTypeBadge type={sheet.jobType} size="sm" />
            {sheet.customerId && (
              <>
                {" · "}
                <Link to={`/office/customers/${sheet.customerId}`} className="text-ele-purple hover:underline">
                  {sheet.customerName ?? "Customer"}
                </Link>
                <AccountSymbol customerId={sheet.customerId} />
              </>
            )}
            {" · "}
            {sheet.siteId ? (
              <Link to={`/office/sites/${sheet.siteId}`} className="text-ele-purple hover:underline">
                {sheet.siteName}
              </Link>
            ) : (
              sheet.siteName
            )}
          </>
        }
        actions={
          sheet.jobStatus === "pending_approval" ? (
            <ConfirmButton
              onConfirm={approveNow}
              disabled={approving}
              busy={approving}
              label="Release"
              busyLabel="Releasing..."
              confirmLabel="Release this record?"
              title="Generates the report, files it to Drive and completes the visit. Nothing is sent to the customer, and they cannot open the report, until you press Send email now."
              className={buttonClasses("primary", "md")}
            />
          ) : sheet.jobStatus === "parts_pending" ? (
            // Session AQY (D-AQX-1): resume needs no reason, so it is a plain confirm here too.
            <ConfirmButton
              onConfirm={resumeNow}
              disabled={pauseResumeBusy}
              busy={pauseResumeBusy}
              label="Resume"
              busyLabel="Resuming..."
              title="Moves this job back to in progress and clears the hold reason."
              className={buttonClasses("secondary", "md")}
            />
          ) : (sheet.jobStatus === "scheduled" || sheet.jobStatus === "in_progress") && !pausing ? (
            <button
              type="button"
              onClick={() => setPausing(true)}
              disabled={pauseResumeBusy}
              className="inline-flex items-center justify-center gap-1.5 rounded-ele border border-amber-400 px-4 py-2 font-heading text-sm tracking-wide text-amber-400 transition hover:bg-ele-inset disabled:opacity-50"
            >
              Put on hold
            </button>
          ) : undefined
        }
      />

      {/* Session AQY (D-AQX-1), reasons Session SC9A: the required-reason step for putting a visit on hold, same shape as the Scheduling
          calendar popover's own. One fixed list; Other needs a few words. Nothing is sent to anybody. */}
      {pausing && (
        <div className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-field p-3">
          <label htmlFor="visit-pause-kind" className="mb-1 block text-xs text-ele-body">
            Why is this job on hold?
          </label>
          <select
            id="visit-pause-kind"
            value={pauseHoldReason}
            onChange={(e) => setPauseHoldReason(e.target.value as JobHoldReasonKey)}
            disabled={pauseResumeBusy}
            className="min-h-11 w-full rounded-ele border border-ele-purple-accent bg-ele-bg px-2 py-1 text-sm text-ele-strong outline-none focus:border-ele-green disabled:opacity-50"
          >
            {JOB_HOLD_REASON_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {pauseHoldReason === "other" && (
            <>
              <label htmlFor="visit-pause-reason" className="mb-1 mt-2 block text-xs text-ele-body">
                Say why in a few words
              </label>
              <input
                id="visit-pause-reason"
                type="text"
                autoFocus
                maxLength={500}
                value={pauseReason}
                onChange={(e) => setPauseReason(e.target.value)}
                placeholder="e.g. Key is with the porter, back Monday"
                disabled={pauseResumeBusy}
                className="min-h-11 w-full rounded-ele border border-ele-purple-accent bg-ele-bg px-2 py-1 text-sm text-ele-strong outline-none focus:border-ele-green disabled:opacity-50"
              />
            </>
          )}
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setPausing(false);
                setPauseReason("");
                setPauseHoldReason("awaiting_parts");
              }}
              disabled={pauseResumeBusy}
              className="rounded-ele border border-ele-purple-accent px-3 py-1.5 text-xs disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void pauseNow()}
              disabled={pauseResumeBusy || (pauseHoldReason === "other" && !pauseReason.trim())}
              className="rounded-ele border border-amber-400 px-3 py-1.5 font-heading text-xs tracking-wide text-amber-400 transition hover:bg-ele-inset disabled:opacity-50"
            >
              {pauseResumeBusy ? "Saving..." : "Confirm hold"}
            </button>
          </div>
        </div>
      )}

      {banner && (
        <p
          role={banner.kind === "error" ? "alert" : "status"}
          className={`mt-4 rounded-ele border px-3 py-2 text-sm ${
            banner.kind === "error" ? "border-ele-danger text-ele-danger" : "border-ele-green text-ele-green"
          }`}
        >
          {banner.message}
        </p>
      )}
      {approvalNotes?.invoiceNote && (
        <p role="status" className="mt-2 rounded-ele border border-ele-hairline px-3 py-2 text-sm text-ele-body">
          {approvalNotes.invoiceNote}
        </p>
      )}
      {approvalNotes?.timesheetNote && (
        <div role="alert" className="mt-2 rounded-ele border border-amber-500 bg-amber-500/10 p-3">
          <p className="text-sm text-amber-300">{approvalNotes.timesheetNote}</p>
        </div>
      )}
      {approvalNotes && approvalNotes.defectsToReview.length > 0 && (
        <div className="mt-2">
          <RemedialDefectOffer defects={approvalNotes.defectsToReview} />
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ServiceRecordPanel
            sheet={sheet}
            onSent={(jobId, sentAt, emailSentTo) =>
              setSheet((s) => (s && s.jobId === jobId ? { ...s, sentToClientAt: sentAt, ...(emailSentTo !== undefined ? { emailSentTo } : {}) } : s))
            }
            onApproved={(jobId, res) =>
              setSheet((s) =>
                s && s.jobId === jobId
                  ? { ...s, jobStatus: "complete", emailSentAt: new Date().toISOString(), emailSentTo: res.emailSentTo, driveFileUrl: res.driveFileUrl }
                  : s,
              )
            }
            onChanged={() => void load()}
            onAmended={() => void load()}
          />
        </div>

        <div className="space-y-6">
          {/* Session BBJ: a visit on a lift with no live contract offers a new contract. */}
          <LiftContractDoor hasLift={sheet.assetId != null} liftContract={sheet.liftContract} customerId={sheet.customerId} siteId={sheet.siteId} />

          <RecordLinkGroup title="Related" empty="Nothing linked to this record yet.">
            {sheet.assetId && <RecordLink key="lift" to={`/office/lifts/${sheet.assetId}`} label={sheet.ref} meta="Lift" />}
            {sheet.siteId && <RecordLink key="site" to={`/office/sites/${sheet.siteId}`} label={sheet.siteName} meta="Site" />}
            {sheet.customerId && (
              <RecordLink key="customer" to={`/office/customers/${sheet.customerId}`} label={sheet.customerName ?? "Customer"} meta="Customer" />
            )}
            {sheet.contractId && (
              <RecordLink key="contract" to={`/office/contracts/${sheet.contractId}`} label="View the covering contract" meta="Contract" />
            )}
          </RecordLinkGroup>

          <section className="rounded-ele border border-ele-hairline bg-ele-surface p-4">
            <h2 className="font-heading text-lg tracking-wide text-ele-strong">Activity</h2>
            <div className="mt-3">
              <AuditTimeline entries={entries} loading={auditLoading} error={auditError} />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
